import * as utils from '@wix/santa-editor-utils';
import experiment from 'experiment';
import _ from 'lodash';
import type { DSRead, CompRef } from 'types/documentServices';
import serviceTopology from '../serviceTopology/serviceTopology';
import UrlBuilder from '../url/UrlBuilder';
import { translate } from '#packages/i18n';
import type { EditorAPI } from '#packages/editorAPI';
import {
  getComponentScopeOwner,
  isComponentScoped,
} from '#packages/documentServices';
import type { AppData } from '#packages/privateAppsPanel';

interface CurrentUrlParams {
  editorType?: 'RESPONSIVE' | 'CLASSIC';
  withInitialWidget?: boolean;
  http_referrer?: string;
}

const controllerTypes = [
  'platform.components.AppWidget',
  'platform.components.AppController',
];

export const httpReferrerOptions = {
  AppImportPanel: 'AppImportPanel',
  LeftTree: 'LeftTree',
  PrivateAppPanel: 'PrivateAppPanel',
};

const getInitialAppStudioModel = () => window.appStudioModel;

/**
 * @deprecated use `shell.getAPI(EditorParamsApi).isInsideAppStudio`
 */
const isAppStudio = () => getInitialAppStudioModel() !== undefined;

const getAppDefIdIfExists = () => getInitialAppStudioModel()?.devSiteAppDefId;

const getNamespace = () => getInitialAppStudioModel()?.namespace;

const getAppVersionOnLoad = () => getInitialAppStudioModel()?.appVersion;
const isAppPublishedOnLoad = () => {
  if (isAppStudio()) {
    const appVersion = getInitialAppStudioModel()?.appVersion;
    if (appVersion) {
      return (
        getVersion(appVersion).major > 0 || getVersion(appVersion).minor > 0
      );
    }
  }
  return false;
};

const getApplicationNameOnLoad = () => getInitialAppStudioModel()?.appName;

const getVersion = (version: string) => {
  if (!version) {
    return {
      major: 0,
      minor: 0,
    };
  }
  const splitVersion = version.split('.').map((v) => parseInt(v, 10));
  return {
    major: splitVersion[0],
    minor: splitVersion[1],
  };
};

const formatVersion = (version: string) => {
  if (version === 'latest') {
    return translate(
      'blocks-private-apps.AppDiscovery_App_Details_Latest_Version_Label',
    );
  }
  const { major, minor } = getVersion(version);
  return `v${major}.${minor}`;
};

const paramsToPassToAppStudio = [
  'ReactSource',
  'EditorSource',
  'debug',
  'debugViewer',
  'WixCodeRuntimeSource',
];

export const blocksNewServerBaseUrl = `${serviceTopology.editorRootUrl}_api/wix-blocks-service/web/v1`;
const blocksNewAppCreatorUrl =
  'https://blocks.wix.com/wix-blocks-new-app-creator';
const welcomeScreen = 'https://dev.wix.com/apps-templates';

const createEditAppURL = (
  app: { appDefinitionId: string },
  httpReferrer: string,
) => {
  httpReferrer += getHttpReferrer();
  return appendCurrentUrlParams(
    `${blocksNewServerBaseUrl}/apps/${app.appDefinitionId}/open`,
    {
      http_referrer: httpReferrer,
    },
  );
};

const isAppMajorVersionBuilt = (latestVersion = '0.0.0') => {
  const { minor, major } = getVersion(latestVersion);
  return !(major === 0 && minor === 0);
};

export enum AppType {
  Widgets = 'widgets',
  CodePackage = 'code_package',
  Combined = 'combined',
  Dashboard = 'dashboard',
}

export const getAppType = (app: AppData) => {
  if (
    !app.compTypes.includes('STUDIO_WIDGET') &&
    app.compTypes.includes('BACK_OFFICE_PAGE')
  ) {
    return AppType.Dashboard;
  }
  if (app.compTypes.includes('CODE_PACKAGE')) {
    if (app.compTypes.includes('STUDIO_WIDGET')) {
      return AppType.Combined;
    }

    return AppType.CodePackage;
  }

  return AppType.Widgets;
};

export const isBlocksApp = (app: AppData) => app.compTypes.includes('STUDIO');

export const isAppNotEmpty = (app: AppData) => {
  return hasStudioWidget(app) || hasDashboardPage(app) || hasCodePackage(app);
};

const hasStudioWidget = (app: AppData) =>
  app.compTypes.includes('STUDIO_WIDGET');
const hasDashboardPage = (app: AppData) =>
  app.compTypes.includes('BACK_OFFICE_PAGE');
const hasCodePackage = (app: AppData) => app.compTypes.includes('CODE_PACKAGE');

const isResponsiveBlocksWidget = (
  { dsRead }: { dsRead: DSRead },
  compRef: CompRef,
) => {
  const compData = dsRead.components.data.get(compRef);
  if (compData?.type === 'WidgetRef') {
    const appData = dsRead.platform.getAppDataByAppDefId(
      compData.appDefinitionId,
    );
    const widgetData = appData?.components?.find?.(
      (comp) => comp.componentId === compData.widgetId,
    );
    //@ts-expect-error
    return isResponsiveBlocksVersion(widgetData?.data?.blocksVersion);
  }
  return false;
};

const isResponsiveBlocksVersion = (blocksVersion: string) =>
  Boolean(blocksVersion?.startsWith('2.'));

const hasMajorVersion = (version: string) => {
  const { major } = getVersion(version);
  return major > 0;
};

const versionToInstall = ({
  appVersion,
  blocksVersion,
}: {
  appVersion: string;
  blocksVersion: string;
}) => {
  // can't use semver ^ for apps with no major version
  if (isResponsiveBlocksVersion(blocksVersion) && hasMajorVersion(appVersion)) {
    return formatSemver(appVersion);
  }

  if (!utils.isResponsiveEditor()) {
    return appVersion;
  }
};

const formatSemver = (version: string, isSemver = true) =>
  `${isSemver ? '^' : ''}${version}`;

const getBlocksVersion = (appData: AppData) => {
  const studioWidgetComponent = appData?.components?.find(
    ({ type }) => type === 'STUDIO_WIDGET',
  );

  if (!studioWidgetComponent) {
    // only Responsive Blocks (2.0.0) supports apps with CODE_PACKAGE or BACK_OFFICE_PAGE component
    const isResponsiveBlocks = appData?.components?.some(({ type }) =>
      ['CODE_PACKAGE', 'BACK_OFFICE_PAGE'].includes(type),
    );

    if (isResponsiveBlocks) return '2.0.0';
  }

  //@ts-expect-error
  return studioWidgetComponent?.data?.blocksVersion;
};

const canImport = (app: AppData) => {
  const { blocksVersion, latestVersion } = app;

  if (!isAppMajorVersionBuilt(latestVersion)) {
    return false;
  }

  const isResponsiveApp =
    isResponsiveBlocksVersion(blocksVersion) ||
    getAppType(app) !== AppType.Widgets;

  if (utils.isResponsiveEditor()) {
    return isResponsiveApp;
  }

  return true;
};

export enum DisplayMode {
  OnlyWidgets = 'only_widgets',
  OnlyCodePackages = 'only_code_packages',
  All = 'all',
}

const filterAppsByDisplayMode = (
  apps: AppData[],
  displayMode: DisplayMode = DisplayMode.All,
) => {
  return apps.filter((app) => {
    switch (displayMode) {
      case DisplayMode.OnlyWidgets:
        return getAppType(app) === AppType.Widgets;
      case DisplayMode.OnlyCodePackages:
        return getAppType(app) === AppType.CodePackage;
      case DisplayMode.All:
      default:
        return true;
    }
  });
};

const appendCurrentUrlParams = (url: string, params: CurrentUrlParams) => {
  const currentUrl = window.document.location.href;
  const editorQuery = utils.url.parseUrl(currentUrl).query;
  const builder = new UrlBuilder(url)
    .paramsObj(_.pick(editorQuery, paramsToPassToAppStudio))
    .paramsObj(params);

  return builder.url();
};

const getNewCodeReuseBlocksURL = (httpReferrer: string) => {
  return appendCurrentUrlParams(blocksNewAppCreatorUrl, {
    editorType: 'RESPONSIVE',
    http_referrer: httpReferrer,
  });
};

const getNewAppStudioURL = (isWixUser: boolean, httpReferrer?: string) => {
  const shouldOpenResponsiveBlocks =
    utils.isResponsiveEditor() ||
    isWixUser ||
    experiment.isOpen('se_openNewResponsiveBlocksFromEditor') ||
    experiment.isOpen('se_privateAppsPanel');

  return appendCurrentUrlParams(
    blocksNewAppCreatorUrl,
    shouldOpenResponsiveBlocks
      ? {
          editorType: 'RESPONSIVE',
          withInitialWidget: true,
          http_referrer: httpReferrer,
        }
      : { editorType: 'CLASSIC', http_referrer: httpReferrer },
  );
};

export enum AppStudioMode {
  App = 'app',
  Package = 'package',
}

const getHttpReferrer = () => {
  if (utils.isOnWixStudio()) {
    return '_wixStudio';
  }

  if (utils.isOnEditorX()) {
    return '_editorx';
  }

  return '_wixEditor';
};

const getBlocksUrl = (
  mode: AppStudioMode = AppStudioMode.App,
  isWixUser: boolean,
  httpReferrer: string,
  isAppStudioAccount: boolean,
) => {
  httpReferrer += getHttpReferrer();

  if (experiment.isOpen('se_blocksWelcomeScreen') || isAppStudioAccount) {
    const url = new URL(welcomeScreen);
    url.searchParams.append('http_referrer', httpReferrer);
    url.searchParams.append('hideSidebar', 'true');
    url.searchParams.append('filter', 'blocks');
    return url.toString();
  }

  switch (mode) {
    case AppStudioMode.Package:
      return getNewCodeReuseBlocksURL(httpReferrer);
    case AppStudioMode.App:
    default:
      return getNewAppStudioURL(isWixUser, httpReferrer);
  }
};

interface OpenBlocksParams {
  mode: AppStudioMode;
  isWixUser: boolean;
  httpReferrer: string;
  isStudioAccount: boolean;
}
const openBlocks = ({
  mode = AppStudioMode.App,
  isWixUser,
  httpReferrer,
  isStudioAccount,
}: OpenBlocksParams) => {
  const url = getBlocksUrl(mode, isWixUser, httpReferrer, isStudioAccount);
  window.open(url, '_blank');
};

const isWidgetController = (
  editorAPI: EditorAPI,
  compRefs: CompRef | CompRef[],
) => {
  compRefs = _.castArray(compRefs);

  if (
    compRefs.length > 1 ||
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    !_.includes(controllerTypes, editorAPI.components.getType(compRefs))
  ) {
    return false;
  }

  const controllerData = editorAPI.dsRead.components.data.get(compRefs[0]);
  const { type: widgetType } =
    editorAPI.dsRead.platform.controllers.settings.get(compRefs[0]);
  const appData = editorAPI.dsRead.platform.getAppDataByAppDefId(
    controllerData.applicationId,
  );
  const widgets = appData?.widgets ?? {};
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/find
  const widget = _.find(
    widgets,
    //@ts-expect-error
    (w) => w?.componentFields?.appStudioFields?.id === widgetType,
  );

  return !!widget;
};

const getWidgetSchema = (
  editorAPI: EditorAPI,
  controllerRef: CompRef,
): [] | { propertiesSchemas: Record<string, any>[] } => {
  const controllerData = editorAPI.dsRead.components.data.get(controllerRef);
  if (!controllerData) {
    return [];
  }

  const { type: widgetType } =
    editorAPI.dsRead.platform.controllers.settings.get(controllerRef);

  const appId = controllerData.applicationId;
  const manifest = editorAPI.platform.getAppManifest(appId);
  //@ts-expect-error
  const widgets = manifest?.widgets;

  return {
    propertiesSchemas: widgets?.[widgetType]?.propertiesSchema,
  };
};

const isAppWidget = (editorAPI: EditorAPI, comp: CompRef) =>
  editorAPI.components.getType(comp) === 'platform.components.AppWidget';

const getRootAppWidget = (editorAPI: EditorAPI) => {
  const currentPage = editorAPI.pages.getCurrentPage();
  return editorAPI.dsRead.appStudio.widgets.getRootWidgetByPage(currentPage);
};

const getWidgetRoot = (editorAPI: EditorAPI) => {
  const rootAppWidget = getRootAppWidget(editorAPI);
  return _.head(
    editorAPI.components.getChildren_DEPRECATED_BAD_PERFORMANCE(rootAppWidget),
  );
};

const getCustomDefinitions = (editorAPI: EditorAPI, controllerRef: CompRef) => {
  const controllerData = editorAPI.dsRead.components.data.get(controllerRef);
  if (!controllerData) {
    return {};
  }
  const appId = controllerData.applicationId;
  const manifest = editorAPI.platform.getAppManifest(appId);
  //@ts-expect-error
  return manifest?.customDefinitions ?? {};
};

const isAppController = (
  editorAPI: EditorAPI,
  compRefs: CompRef | CompRef[],
) => {
  compRefs = _.castArray(compRefs);
  return (
    compRefs.length <= 1 &&
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    _.includes(controllerTypes, editorAPI.components.getType(compRefs))
  );
};

const isMajorUpdate = (oldVersion: string, newVersion: string) => {
  return getVersion(oldVersion)?.major !== getVersion(newVersion)?.major;
};

const isWixUser = (dsRead: DSRead) =>
  dsRead.generalInfo.getUserInfo().email?.endsWith('@wix.com');

const getScopedCompRef = (editorAPI: EditorAPI, componentRef: CompRef) => {
  if (
    editorAPI.dsRead.viewMode.get() !==
    editorAPI.dsRead.viewMode.VIEW_MODES.MOBILE
  ) {
    return componentRef;
  }

  const mobileVariant = editorAPI.mobile.getMobileVariant();
  if (!mobileVariant) {
    return;
  }
  const compVariantRef = editorAPI.components.variants.getPointer(
    componentRef,
    mobileVariant,
  );

  return compVariantRef;
};

const getPresetForInnerWidget = async (
  editorAPI: EditorAPI,
  compRef: CompRef,
) => {
  const componentRef = { ...compRef } as CompRef;
  const componentPage =
    editorAPI.documentServices.components.getPage(componentRef);

  const outerWidgetRef = getComponentScopeOwner(editorAPI.dsRead, componentRef);

  let outerPreset =
    await editorAPI.dsRead.appStudioWidgets.presets.get(outerWidgetRef);

  if (!outerPreset && !isComponentScoped(editorAPI.dsRead, outerWidgetRef)) {
    return null;
  }

  if (!outerPreset) {
    outerPreset = await getPresetForInnerWidget(editorAPI, outerWidgetRef);
  }

  if (outerPreset) {
    // todo replace with DM API when available. For now we don't have appropriate DM API for create Variant pointer.
    componentRef.variants = [
      {
        id: outerPreset.layout,
        type: 'variants',
        pageId: componentPage.id,
      },
    ];
  }

  return editorAPI.dsRead.appStudioWidgets.presets.get(componentRef);
};

export {
  isAppWidget,
  isWixUser,
  createEditAppURL,
  isAppPublishedOnLoad,
  isAppStudio,
  isResponsiveBlocksVersion,
  getAppDefIdIfExists,
  isWidgetController,
  getNewAppStudioURL,
  getWidgetRoot,
  getRootAppWidget,
  getWidgetSchema,
  getCustomDefinitions,
  getApplicationNameOnLoad,
  getAppVersionOnLoad,
  isAppController,
  getNamespace,
  filterAppsByDisplayMode,
  isAppMajorVersionBuilt,
  getNewCodeReuseBlocksURL,
  openBlocks,
  versionToInstall,
  getBlocksVersion,
  isResponsiveBlocksWidget,
  isMajorUpdate,
  canImport,
  formatVersion,
  formatSemver,
  getVersion,
  getPresetForInnerWidget,
  getScopedCompRef,
};
